import { Account } from 'shared/types/account';
import { LineItem } from 'shared/types/cart';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { TealiumUTag } from 'shared/types/tealium/TealiumUTag';
import { CurrencyHelpers } from '@/helpers/utils/currencyHelpers';
import { setNewRelicPageAttributes } from '@/helpers/utils/newRelicUtils';
import { getSelectedDivision, getCustomerEmail, getSelectedSite } from '@/helpers/utils/siteUtils';
import * as CONSTANTS from '../../../constants/constant';
import { getFilteredAttribute } from '../../customercenter-ui/components/product/details/helpers/utils/productDataUtils';

interface ProductInfo {
  productData?: any;
  product?: any;
  initialColor?: any;
  initialSize?: any;
  quantity?: number;
}
export const getPageUtagData = (
  pageName: string,
  searchType?: string,
  account?: Account | null,
  productInfo?: ProductInfo,
  data?: any,
  categoryData?: any,
  pageURL?: any,
  projectSettings?: ProjectSettings,
): TealiumUTag => {
  try {
    const currency = CONSTANTS.CURRENCY;
    const language = CONSTANTS.TEALIUM_LANGUAGE;
    const currentSite = getSelectedDivision() + CONSTANTS.SITE;

    const uTag: TealiumUTag = {
      page_name: pageName,
      site_currency: currency,
      site_region: language,
    };

    switch (pageName) {
      case CONSTANTS.CUSTOMER_HOME_PAGE_NAME:
        uTag.page_type = CONSTANTS.CUSTOMER_SEARCH_PAGE_TYPE;
        break;
      case CONSTANTS.ORDER_HOME_PAGE_NAME:
        uTag.page_type = CONSTANTS.ORDER_SEARCH_PAGE_TYPE;
        break;
      case CONSTANTS.CUSTOMER_SEARCH_PAGE_NAME:
        uTag.page_type = CONSTANTS.CUSTOMER_SEARCH_PAGE_TYPE;

        if (searchType && searchType?.indexOf('zip') > -1) {
          uTag.customer_search_type = CONSTANTS.CUSTOMER_ZIP_CODE_SEARCH;
        } else {
          uTag.customer_search_type = searchType?.replaceAll('-', ' ');
        }
        break;
      case CONSTANTS.CUSTOMER_DETAILS_PAGE_NAME:
        uTag.page_type = CONSTANTS.CUSTOMER_INFO_PAGE_TYPE;
        break;
      case CONSTANTS.ORDER_DETAILS_PAGE_NAME:
        uTag.page_type = CONSTANTS.ORDER_INFO_PAGE_TYPE;
        break;
      case CONSTANTS.PRODUCT_CATALOG_PAGE_NAME:
        categoryData?.hits && getPLPUtagData(uTag, categoryData);

        uTag.division = currentSite;
        if (getCustomerEmail()) {
          uTag.customer_email = getCustomerEmail();
        }
        break;
      case CONSTANTS.TEALIUM_PAGE_NAME_NO_RESULTS:
        categoryData?.hits && getSearchPageUtagData(uTag, categoryData);
        uTag.page_type = CONSTANTS.TEALIUM_PAGE_TYPE_SEARCH;
        break;

      case CONSTANTS.PDP_PAGE_NAME:
        productInfo && pdpUtagData(uTag, productInfo, projectSettings);
        uTag.division = currentSite;
        if (getCustomerEmail()) {
          uTag.customer_email = getCustomerEmail();
        }
        break;
      case CONSTANTS.TEALIUM_PAGE_BASKET:
        data && cartUtagData(uTag, data, projectSettings);
        uTag.division = currentSite;
        if (getCustomerEmail()) {
          uTag.customer_email = getCustomerEmail();
        }
        break;
      case CONSTANTS.TEALIUM_PAGE_SHIPPING_PAYMENT_REVIEW:
        uTag.page_type = CONSTANTS.TEALIUM_PAGE_TYPE_CHECKOUT;
        uTag.division = currentSite;
        if (getCustomerEmail()) {
          uTag.customer_email = getCustomerEmail();
        }
        break;
      case CONSTANTS.TEALIUM_PAGE_NAME_CONFIRMATION:
        data && orderConfirmationUtagData(uTag, data);
        uTag.customer_no = data?.webAccountId ?? '';
        break;

      default:
    }
    return uTag;
  } catch (error) {
    console.error('Error in getting UTAG page data ', error);
    return {};
  }
};

export const getSearchPageUtagData = (uTag: TealiumUTag, categoryData: any) => {
  try {
    const productIds: string[] = [];
    const productABWList: string[] = [];
    const productParents: string[] = [];
    const productCategoryGA: string[] = [];
    const productNames: string[] = [];
    const producBrands: string[] = [];
    const productPosition: string[] = [];
    const productList: string[] = [];
    const productDivisionList: any[] = [];
    categoryData?.hits?.slice(0, 10)?.forEach((product: any) => {
      productIds.push(product?.key);
      productABWList.push(product?.abwStyle);
      productParents.push(product?.stiboDsku);
      if (product?.brandName) {
        producBrands.push(product?.brandName);
      } else {
        producBrands.push('');
      }
      productNames.push(product?.displayName);
      productCategoryGA.push(product?.department + '/' + product?.class + '/' + product?.subClass);
      productPosition.push(product?.__position);
      productDivisionList.push(uTag?.current_division);
      if (uTag.page_name == CONSTANTS.TEALIUM_PAGE_NAME_RESULTS) {
        productList.push(CONSTANTS.TEALIUM_SEARCH_RESULT);
      }
      if (uTag.page_name == CONSTANTS.TEALIUM_PAGE_NAME_BRAND) {
        productList.push(CONSTANTS.TEALIUM_BRAND_LISTING);
      }
    });
    uTag.product_parent_id = productParents;
    uTag.product_abw_id = productABWList;
    uTag.product_id = productIds;
    uTag.product_category_ga = productCategoryGA;
    uTag.product_name = productNames;
    uTag.optimizely_product_name = productNames;
    uTag.product_brand = producBrands;
    uTag.product_position = productPosition;
    uTag.search_results = categoryData?.nbHits;
    uTag.search_keyword = categoryData?.searchQuery;
    uTag.product_division = productDivisionList;
    uTag.product_list = productList;
  } catch (error) {
    throw error;
  }
};

export const getPLPUtagData = (uTag: TealiumUTag, categoryData: any) => {
  try {
    const productIds: string[] = [];
    const productABWList: string[] = [];
    const productParents: string[] = [];
    const appliedRefinment: string[] = [];
    const productCategoryGA: string[] = [];
    const productNames: string[] = [];
    const producBrands: string[] = [];
    const productPosition: string[] = [];
    const productList: string[] = [];
    const productDivisionList: any[] = [];
    categoryData?.hits?.slice(0, 10)?.forEach((product: any) => {
      productIds.push(product?.key);
      productABWList.push(product?.abwStyle);
      productParents.push(product?.stiboDsku);
      if (product?.brandName) {
        producBrands.push(product?.brandName);
      } else {
        producBrands.push('');
      }
      productNames.push(product?.displayName);
      productCategoryGA.push(product?.department + '/' + product?.class + '/' + product?.subClass);
      productPosition.push(product?.__position);
      productDivisionList.push(getSelectedDivision());
      if (categoryData?.searchQuery) {
        productList.push(CONSTANTS.TEALIUM_SEARCH_RESULT);
      } else {
        productList.push(CONSTANTS.TEALIUM_PRODUCT_LISTING);
      }
    });
    categoryData?.items?.forEach((item: any) => {
      item?.refinements?.forEach((refin: any) => {
        appliedRefinment.push(refin?.value);
      });
    });

    uTag.page_section_name = categoryData?.categoryHit?.breadcrumbs?.[0]?.name;
    uTag.page_category_name = categoryData?.categoryHit?.breadcrumbs?.[1]?.name;

    if (categoryData?.categoryHit?.name) {
      uTag.page_name = categoryData?.categoryHit?.name?.[CONSTANTS.TEALIUM_PRODUCT_EN_US];
      uTag.page_subcategory_name = categoryData?.categoryHit?.name?.[CONSTANTS.TEALIUM_PRODUCT_EN_US];
    }

    uTag.product_parent_id = productParents;
    uTag.product_abw_id = productABWList;
    uTag.product_id = productIds;
    uTag.product_category_ga = productCategoryGA;
    uTag.product_name = productNames;
    uTag.optimizely_product_name = productNames;
    uTag.product_brand = producBrands;
    uTag.product_position = productPosition;
    uTag.product_division = productDivisionList;
    uTag.product_list = productList;
    uTag.product_category_id = categoryData?.categoryHit?.objectID;
    if (appliedRefinment.length > 0) {
      uTag.page_refinement = appliedRefinment;
    }
    if (categoryData?.searchQuery) {
      uTag.search_keyword = categoryData?.searchQuery;
      uTag.page_type = CONSTANTS.SEARCH_PAGE_TYPE;
      uTag.page_name = CONSTANTS.RESULT_PAGE_TYPE;
      uTag.search_results = categoryData?.nbHits;
    } else {
      uTag.page_type = CONSTANTS.CATEGORY_PAGE_TYPE;
    }
  } catch (error) {
    throw error;
  }
};

export const pdpUtagData = (
  uTag: TealiumUTag,
  productInfo: ProductInfo | undefined,
  projectSettings: ProjectSettings | undefined,
) => {
  try {
    const productData = productInfo?.productData;
    const product = productInfo?.product;
    const initialColor = productInfo?.initialColor;
    const siteId = getSelectedSite() ?? 'ZBSite';
    const imageHost = JSON.parse(projectSettings?.imageHost ?? '{}')?.[`${getSelectedDivision() ?? 'ZB'}`];
    const productId = product?.productId ?? initialColor?.key;
    const productName = product?.name ?? initialColor?.name;
    const stiboDSku: string = productData?.results?.[0]?.key?.split('-')?.[0];
    const imageURL: string = getFilteredAttribute(CONSTANTS.MAIN_IMAGE_URL, initialColor?.masterVariant);
    const departmentName: string = getFilteredAttribute(CONSTANTS.DEPARTMENT, initialColor?.masterVariant);
    const className: string = getFilteredAttribute(CONSTANTS.CLASS, initialColor?.masterVariant);
    const subClassName: string = getFilteredAttribute(CONSTANTS.SUB_CLASS, initialColor?.masterVariant);

    const currentProduct = productData?.results?.filter((product: any) => product?.key == productId)?.[0];

    const lastVisitedPLP_SRP = sessionStorage.getItem('lastVisitedPLP_SRP') || CONSTANTS.TEALIUM_DEFAULT_CATEGORY;

    let selectedBreadCrumbString =
      productData?.hierarchalCategories?.filter(
        (obj: string) => obj?.toLocaleLowerCase()?.indexOf(siteId?.replace('Site', '')?.toLocaleLowerCase() || '') > -1,
      )?.[0] || '';
    const breadcrumbTemp: any = [];
    let matchesFound = false;
    productData?.hierarchalCategories?.map((crumb: string) => {
      let matches = 0;
      const slugArray = lastVisitedPLP_SRP?.split('|');
      if (slugArray?.length) {
        slugArray?.map((cat: string) => {
          if (
            crumb?.toLocaleLowerCase()?.indexOf(cat?.replace(/\-/g, ' ')?.replace(/\'/g, '')?.replace('and', '+')) > -1
          ) {
            matches++;
          }
        });
      }
      if (
        lastVisitedPLP_SRP !== '' &&
        slugArray?.length === matches &&
        crumb?.toLocaleLowerCase()?.indexOf(siteId?.replace('Site', '')?.toLocaleLowerCase() || '') > -1
      ) {
        selectedBreadCrumbString = crumb;
        matchesFound = true;
      }
    });
    let stopAddingItems = false;
    selectedBreadCrumbString?.split(' > ')?.map((item: string) => {
      if (!stopAddingItems) {
        const catDetails = product?.categories?.filter(
          (obj: any) => obj?.key === item?.match(/\(([0-9A-Za-z\-]+)\)/)?.[1],
        )?.[0];
        if (catDetails) {
          breadcrumbTemp?.push(catDetails);
        }
      }
      if (
        item
          ?.replace(/\(([0-9A-Za-z\-]+)\)/, '')
          ?.replace(/\'/g, '')
          ?.toLocaleLowerCase()
          ?.trim() ===
          lastVisitedPLP_SRP
            ?.split('|')
            ?.[lastVisitedPLP_SRP?.split('|')?.length - 1]?.replace(/\-/g, ' ')
            ?.replace('and', '+')
            ?.toLocaleLowerCase() &&
        (lastVisitedPLP_SRP !== '' || matchesFound)
      ) {
        stopAddingItems = true;
      }
    });

    const categories = breadcrumbTemp?.map((crumb: any) => crumb?.name);
    const pageSection: string = categories?.[0] ?? '';
    const category: string = categories?.[1] ?? '';
    const subCategory: string = categories?.[2] ?? '';

    uTag.page_type = CONSTANTS.TEALIUM_PAGE_TYPE_PRODUCT;
    uTag.page_name = productName;
    uTag.product_sub_category = [subClassName];
    uTag.product_image = imageHost + imageURL;
    uTag.page_section_name = pageSection;
    uTag.page_category_name = category;
    uTag.page_subcategory_name = subCategory;
    uTag.product_parent_id = [stiboDSku];
    uTag.product_abw_id = [getFilteredAttribute(CONSTANTS.ABWSTYLE, initialColor?.masterVariant)];
    uTag.product_brand = [getFilteredAttribute(CONSTANTS.BRAND_NAME, initialColor?.masterVariant)];
    uTag.product_category = [className];
    uTag.product_id = [productId];
    uTag.product_name = [productName];
    uTag.product_sku = [''];
    uTag.optimizely_product_name = productName;
    uTag.product_category_ga = [departmentName + '/' + className + '/' + subClassName];
    setPDPPriceTag(uTag, currentProduct);
  } catch (error) {
    throw error;
  }
};

export const cartUtagData = (uTag: TealiumUTag, data: any, projectSettings: ProjectSettings | undefined) => {
  try {
    const carttotal = CurrencyHelpers.formatForCurrency({ ...data?.cartTotal }).substring(1);
    const productIds: string[] = [];
    const producBrands: string[] = [];
    const subCategories: string[] = [];
    const categories: string[] = [];
    const productParents: string[] = [];
    const productABWList: string[] = [];

    const productSkus: string[] = [];
    const productNames: string[] = [];
    const productQuantityList: string[] = [];
    const productListPrice: string[] = [];
    const productUnitList: string[] = [];

    const productImageUrls: string[] = [];
    const imageHost = JSON.parse(projectSettings?.imageHost ?? '{}')?.[`${getSelectedDivision() ?? 'ZB'}`];
    if (data?.lineItems?.length > 0) {
      data?.lineItems?.forEach((item: any) => {
        productIds.push(item?.productKey);
        producBrands.push(item?.variant?.attributes?.brandName);
        categories.push(item?.variant?.attributes?.class);
        subCategories.push(item?.variant?.attributes?.subClass);
        productParents.push(item?.variant?.attributes?.stiboDsku);
        productABWList.push(item?.variant?.attributes?.abwStyle);

        productSkus.push(item?.variant?.sku);
        productNames.push(item?.name);
        productQuantityList.push(item?.count);
        productListPrice.push(CurrencyHelpers.formatForCurrency({ ...item?.listPrice })?.substring(1));
        productUnitList.push(CurrencyHelpers.formatForCurrency({ ...item?.price })?.substring(1));

        productImageUrls.push(imageHost + item?.variant?.attributes?.mainImageURL);
      });
    }

    uTag.order_quantity = productQuantityList?.reduce((a, b) => Number(a) + Number(b), 0);
    uTag.cart_quantity = productQuantityList?.reduce((a, b) => Number(a) + Number(b), 0);
    uTag.cart_total = parseFloat(carttotal);
    uTag.page_type = CONSTANTS.TEALIUM_PAGE_TYPE_CHECKOUT;
    uTag.product_brand = producBrands;
    uTag.product_category = categories;
    uTag.product_sub_category = subCategories;
    uTag.product_parent_id = productParents;
    uTag.product_abw_id = productABWList;
    uTag.product_id = productIds;
    uTag.product_list_price = productListPrice;
    uTag.product_name = productNames;
    uTag.product_quantity = productQuantityList;
    uTag.product_sku = productSkus;
    uTag.product_unit_price = productUnitList;

    uTag.product_image = productImageUrls;
  } catch (error) {
    throw error;
  }
};

export const orderConfirmationUtagData = (uTag: TealiumUTag, data: any) => {
  try {
    const productIds: string[] = [];
    const producBrands: string[] = [];
    const categories: string[] = [];
    const productParents: string[] = [];
    const productABWList: string[] = [];

    const productSkus: string[] = [];
    const productNames: string[] = [];
    const productQuantityList: string[] = [];
    const productListPrice: string[] = [];
    const productUnitList: string[] = [];
    const productPromoCodeList: string[] = [];
    const productDiscountList: string[] = [];
    const promoLevelList: string[] = [];
    let isShippingInsuranceSelected = false;
    let shippingInsurancePrice = '0';
    let itemLevelPromo = true;
    const order_promo_code: string = data?.discountCodes?.[0]?.code;
    if (data?.lineItems?.length > 0) {
      const insuranceLineItem: LineItem | undefined = data?.lineItems?.find((item: LineItem) => item?.isInsuranceItem);
      if (insuranceLineItem) {
        isShippingInsuranceSelected = true;
        if (insuranceLineItem?.price?.centAmount ?? 0 > 0) {
          shippingInsurancePrice = CurrencyHelpers.formatForCurrency({ ...insuranceLineItem?.price })
            ?.substring(1)
            ?.replaceAll(',', '');
        }
      }
      data?.lineItems?.forEach((item: any) => {
        productIds.push(item?.productKey);
        producBrands.push(item?.variant?.attributes?.brandName);
        categories.push(item?.variant?.attributes?.class);
        productParents.push(item?.variant?.attributes?.stiboDsku);
        productABWList.push(item?.variant?.attributes?.abwStyle);
        productSkus.push(item?.variant?.sku);
        productNames.push(item?.name);
        productQuantityList.push(item?.count);
        productListPrice.push(CurrencyHelpers.formatForCurrency({ ...item?.listPrice })?.substring(1));

        productUnitList.push(CurrencyHelpers.formatForCurrency({ ...item?.price })?.substring(1));
        if (item?.discounts?.length > 0) {
          item?.discounts?.forEach((discount: any) => {
            productDiscountList.push(CurrencyHelpers.formatForCurrency({ ...discount?.discountedAmount }));
          });
        }
      });
    }
    if (order_promo_code) {
      uTag.order_promo_code = order_promo_code;
      const promotionId = data?.discountCodes?.[0]?.promotionId;
      const discountType = data?.discountTypes?.find((discountType: any) => discountType?.promotionId === promotionId);
      if (discountType?.target) {
        itemLevelPromo = false;
      }
      if (itemLevelPromo) {
        productPromoCodeList.push(order_promo_code);
        promoLevelList.push(CONSTANTS.TEALIUM_ITEM_LEVEL);
      } else {
        promoLevelList.push(CONSTANTS.TEALIUM_ORDER_LEVEL);
        productDiscountList.push(CurrencyHelpers.formatForCurrency({ ...data?.discountTotal }));
      }
    }

    uTag.product_promo_code = productPromoCodeList;
    uTag.product_discount = productDiscountList;
    uTag.promo_level = promoLevelList;
    uTag.page_type = CONSTANTS.TEALIUM_PAGE_TYPE_CHECKOUT;
    uTag.product_brand = producBrands;
    uTag.product_category = categories;
    uTag.product_parent_id = productParents;
    uTag.product_abw_id = productABWList;
    uTag.product_id = productIds;
    uTag.product_list_price = productListPrice;
    uTag.product_name = productNames;
    uTag.product_quantity = productQuantityList;
    uTag.product_sku = productSkus;
    uTag.product_unit_price = productUnitList;

    uTag.product_discount = productDiscountList;
    uTag.order_currency = CONSTANTS.CURRENCY;
    uTag.order_id = data?.orderNumber;
    uTag.customer_email = data?.email;

    uTag.order_quantity = productQuantityList?.reduce((a, b) => Number(a) + Number(b), 0);
    const paymentMethod = data?.paymentDetails?.paymentMethod ?? data?.orderType;
    if (paymentMethod === CONSTANTS.TEALIUM_PAYMENT_METHOD_PAYPAL) {
      uTag.payment_type = CONSTANTS.TEALIUM_PAYMENT_TYPE_PAYPAL;
    } else if (paymentMethod === CONSTANTS.TEALIUM_PAYMENT_METHOD_CREDIT) {
      uTag.payment_type = CONSTANTS.TEALIUM_PAYMENT_TYPE_CREDIT;
    } else if (paymentMethod === CONSTANTS.TEALIUM_PAYMENT_METHOD_CARD) {
      uTag.payment_type = CONSTANTS.TEALIUM_PAYMENT_TYPE_CARD;
    } else if (paymentMethod === CONSTANTS.TEALIUM_PAYMENT_METHOD_CASH) {
      uTag.payment_type = CONSTANTS.TEALIUM_PAYMENT_TYPE_CASH;
    } else if (paymentMethod === CONSTANTS.TEALIUM_PAYMENT_METHOD_CHECK) {
      uTag.payment_type = CONSTANTS.TEALIUM_PAYMENT_TYPE_CHECK;
    } else if (paymentMethod === CONSTANTS.TEALIUM_PAYMENT_METHOD_NOCHARGE) {
      uTag.payment_type = CONSTANTS.TEALIUM_PAYMENT_TYPE_NOCHARGE;
    }
    uTag.order_total = CurrencyHelpers.formatForCurrency({ ...data?.total })?.substring(1);
    uTag.order_shipping = CurrencyHelpers.formatForCurrency({ ...data?.actualShippingAmount })?.substring(1);
    uTag.order_tax = CurrencyHelpers.formatForCurrency({ ...data?.totalTaxedPrice })?.substring(1);
    uTag.order_discount = CurrencyHelpers.formatForCurrency({ ...data?.discountTotal })?.substring(1);
    uTag.order_subtotal = CurrencyHelpers.formatForCurrency({ ...data?.subTotal })?.substring(1);
    uTag.shipping_protection_selected = isShippingInsuranceSelected;
    if (isShippingInsuranceSelected && shippingInsurancePrice !== '0') {
      uTag.shipping_protection_amount = Number(shippingInsurancePrice);
    }
  } catch (error) {
    throw error;
  }
};

export const setPDPPriceTag = (uTag: TealiumUTag, currentProduct: any) => {
  try {
    const variantsPrice = currentProduct?.variants?.map((variant: any) => variant?.price) ?? [];
    const masterVariantPrice = currentProduct?.masterVariant?.price;
    const allVariantsPrices = [...variantsPrice, masterVariantPrice].filter(Boolean);

    const sellPrice =
      allVariantsPrices.map((sellPrices: any) =>
        CurrencyHelpers.formatForCurrency({ ...sellPrices?.value })?.substring(1),
      ) ?? [];

    const listPrice =
      allVariantsPrices.map((listPrices: any) =>
        CurrencyHelpers.formatForCurrency({ ...listPrices?.listPrice })?.substring(1),
      ) ?? [];

    const uniqueSellPrice = sellPrice?.filter((value, index, array) => array?.indexOf(value) === index);
    const uniqueListPrice = listPrice?.filter((value, index, array) => array?.indexOf(value) === index);
    uTag.product_unit_price = uniqueSellPrice;
    uTag.product_list_price = uniqueListPrice;
    uTag.optimizely_product_list_price = uniqueListPrice;
    uTag.optimizely_product_unit_price = uniqueSellPrice;
  } catch (error) {
    throw error;
  }
};

export const utagViewLoaded = () => {
  if (typeof window !== 'undefined' && window?.$ && window?.utag?.view && (window?.utag_data || window?.utag?.data)) {
    return true;
  } else {
    return false;
  }
};

export const runUtagView = (finalUtag: any) => {
  const finalUtagData = { ...finalUtag };
  window?.utag?.view(finalUtagData);
};

export const _utag_waitForView = (
  pageUtagData: any,
  utagViewLoaded: any,
  runUtagView: any,
  timeInterval: number,
  maxRuns: number,
  thisRun: number,
) => {
  if (maxRuns < thisRun) {
    return;
  }
  if (utagViewLoaded()) {
    runUtagView(pageUtagData);
  } else {
    thisRun++;
    setTimeout(function () {
      _utag_waitForView(pageUtagData, utagViewLoaded, runUtagView, timeInterval, maxRuns, thisRun);
    }, timeInterval);
  }
};

export const triggerTealiumEvent = (pageUtagData: any) => {
  setNewRelicPageAttributes({
    pageType: pageUtagData?.page_type,
    division: getSelectedDivision(),
  });
  setTimeout(() => {
    _utag_waitForView(pageUtagData, utagViewLoaded, runUtagView, 200, 10, 1);
  }, 500);
};
